
import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { ENVIRONMENT, logger } from '@/lib/environment';

export const useAuthActions = () => {
  const signIn = useCallback(async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      });

      if (error) {
        toast({
          title: "Sign in failed",
          description: error.message,
          variant: "destructive",
        });
      }

      return { data, error };
    } catch (error) {
      logger.error('Sign in error:', error);
      return { data: null, error };
    }
  }, []);

  const signUp = useCallback(async (email: string, password: string, firstName: string, lastName?: string, captchaToken?: string, marketingOptOut?: boolean) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password,
        options: {
          data: {
            first_name: firstName,
            ...(lastName && { last_name: lastName }),
            full_name: lastName ? `${firstName} ${lastName}` : firstName,
            // D1: refusal at sign-up. on-user-created reads it, creates the Resend contact already
            // unsubscribed and does not start the welcome automation.
            ...(marketingOptOut && { marketing_opt_out: true }),
          },
          emailRedirectTo: `${window.location.origin}/`,
          captchaToken,
        },
      });

      if (error) {
        toast({
          title: "Sign up failed",
          description: error.message,
          variant: "destructive",
        });
      } else if (data.user && !data.session) {
        // Generate CSRF token for new user
        try {
          const csrfToken = crypto.randomUUID();
          await supabase.auth.updateUser({
            data: { csrf_token: csrfToken }
          });
        } catch (csrfError) {
          logger.error('Failed to generate CSRF token for new user:', csrfError);
          // Don't fail the signup if CSRF token generation fails
        }

        toast({
          title: "Check your email",
          description: "We've sent you a confirmation link.",
        });
      }

      return { data, error };
    } catch (error) {
      logger.error('Sign up error:', error);
      return { data: null, error };
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      // Signal UnifiedAuthProvider to pause refocus while signing out
      try {
        const chStart = new BroadcastChannel('auth-signout');
        chStart.postMessage({ type: 'signout:start' });
        chStart.close();
      } catch {
        // ignore BroadcastChannel errors
      }
      // Immediate local effect: clear stored session and navigate to /auth
      try {
        const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
        if (SUPABASE_URL) {
          const host = new URL(SUPABASE_URL).hostname.split('.')[0];
          const storageKey = `sb-${host}-auth-token`;
          window.localStorage.removeItem(storageKey);
        }
      } catch {
        // ignore
      }
      // Fire-and-forget the network signOut to avoid UI being blocked by refocus
      setTimeout(() => { supabase.auth.signOut().catch(() => {}); }, 0);
      // Broadcast end and navigate immediately so UI reflects signed-out state
      try {
        const ch2 = new BroadcastChannel('auth-signout');
        ch2.postMessage({ type: 'signout:end' });
        ch2.close();
      } catch (e) {
        // ignore BroadcastChannel errors in unsupported environments
      }
      try {
        window.location.assign('/auth');
      } catch {
        // ignore
      }
    } catch (error) {
      logger.error('Sign out error:', error);
      toast({
        title: "Sign out failed",
        description: "There was an error signing you out.",
        variant: "destructive",
      });
    }
  }, []);

  const resetPasswordForEmail = useCallback(async (email: string) => {
    try {
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth?mode=reset-password`,
      });

      if (error) {
        toast({
          title: "Password reset failed",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Check your email",
          description: "We've sent you a password reset link.",
        });
      }

      return { data, error };
    } catch (error) {
      logger.error('Password reset error:', error);
      return { data: null, error };
    }
  }, []);

  const updateEmail = useCallback(async (newEmail: string, currentPassword: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const currentEmail = session?.user?.email?.trim().toLowerCase();
      const normalized = newEmail.trim().toLowerCase();
      if (!currentEmail) {
        return { data: null, error: { message: 'Not authenticated' } };
      }
      if (!normalized || normalized === currentEmail) {
        return { data: null, error: { message: 'Please enter a different email' } };
      }

      // Re-authenticate to verify password
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: currentEmail,
        password: currentPassword,
      });
      if (signInError) {
        return { data: null, error: { message: 'Current password is incorrect', status: signInError.status } };
      }

      // Request email update (Supabase sends verification link)
      const emailRedirectTo = `${window.location.origin}/auth?mode=verify-email`;
      const { data, error } = await supabase.auth.updateUser({ email: normalized }, { emailRedirectTo });
      if (error) {
        const msg = error.message?.toLowerCase?.() || '';
        if (msg.includes('already registered') || msg.includes('in use')) {
          return { data: null, error: { message: 'This email is already in use', status: error.status } };
        }
        return { data: null, error: { message: 'Unable to update email. Please try again.', status: error.status } };
      }
      return { data, error: null };
    } catch (e) {
      return { data: null, error: { message: e instanceof Error ? e.message : 'Unknown error' } };
    }
  }, []);

  const signInWithGoogle = useCallback(async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth`,
        },
      });

      if (error) {
        toast({
          title: "Google sign in failed",
          description: error.message,
          variant: "destructive",
        });
      }

      return { data, error };
    } catch (error) {
      logger.error('Google sign in error:', error);
      return { data: null, error };
    }
  }, []);

  return { signIn, signUp, signOut, resetPasswordForEmail, updateEmail, signInWithGoogle };
};
